/**
 * abilityEffects — client-side visuals for active abilities, keyed by the
 * ability id the server puts in `player.activeAbilityEffects`.
 *
 * Adding a visual for a new ability is one `registerAbilityEffect()` call; the
 * entity renderer never needs another `if (isX)` branch. An ability with no
 * registered effect simply has no visual (today: super_speed, heal, teleport
 * — their feedback comes from other systems).
 *
 * Effect shape:
 *   {
 *     // Optional: how the player is shown while the effect is active.
 *     // Return { visible: false } to hide them entirely, or { alpha } to fade.
 *     // `ctx` is { now, group } when the renderer asks (see resolveAbilityView).
 *     view?: (player, ctx) => ({ visible?: boolean, alpha?: number }),
 *     // Optional per-frame draw while active. `group` is the renderer's sprite
 *     // group (container, rotationContainer, invulnOverlay, …); `ctx` is
 *     // { now, alpha, player }.
 *     onFrame?: (group, ctx) => void,
 *     // Optional: called once when the effect stops being active.
 *     onEnd?: (group) => void,
 *     // Optional: called when the server says the ability started/stopped, for
 *     // the state an ability needs set up before any frame is drawn (trail
 *     // breaks, end times, the teleport trail). `player` may be null if the
 *     // activating player is not known to this client.
 *     onActivated?: (player, data, gameState) => void,
 *     onDeactivated?: (player, data, gameState) => void
 *   }
 */
import { abilityRegistry } from '../../../shared/AbilityRegistry.js';

const registry = new Map();

export function registerAbilityEffect(abilityId, effect) {
  if (!abilityId || !effect) return;
  registry.set(abilityId, effect);
}

export function getAbilityEffect(abilityId) {
  return registry.get(abilityId) || null;
}

/** Ids of the effects currently active on a player (truthy entries only). */
export function activeEffectIds(player) {
  const effects = player?.activeAbilityEffects || {};
  return Object.keys(effects).filter((id) => effects[id]);
}

/**
 * Combine every active effect's `view` into how the player should be drawn.
 * Hidden wins over faded; alphas multiply.
 * @param {object} player
 * @param {{ now?: number, group?: object }} [ctx] frame time and the player's
 *        sprite group, for effects that ease in (state lives on the group, so
 *        a server snapshot replacing activeAbilityEffects cannot reset it)
 * @returns {{ visible: boolean, alpha: number }}
 */
export function resolveAbilityView(player, ctx = {}) {
  let visible = true;
  let alpha = 1;
  for (const id of activeEffectIds(player)) {
    const view = registry.get(id)?.view?.(player, ctx);
    if (!view) continue;
    if (view.visible === false) visible = false;
    if (typeof view.alpha === 'number') alpha *= view.alpha;
  }
  return { visible, alpha };
}

/**
 * Run per-frame effects for a player's sprite group and end the ones that
 * just stopped. Tracks what was active on the group so onEnd fires exactly
 * once per activation.
 */
export function applyAbilityEffects(group, player, ctx) {
  const active = new Set(activeEffectIds(player).filter((id) => registry.has(id)));
  const previous = group._activeAbilityEffects || new Set();

  for (const id of previous) {
    if (!active.has(id)) registry.get(id)?.onEnd?.(group);
  }
  for (const id of active) {
    registry.get(id)?.onFrame?.(group, ctx);
  }
  group._activeAbilityEffects = active;
}

/**
 * Tell an ability's entry that the server activated it.
 *
 * This is what lets `AbilityEvents.js` name no abilities: it dispatches by id
 * and each ability sets up its own state.
 *
 * @param {string} abilityId
 * @param {object|null} player the activating player, if this client knows them
 * @param {object} data the `abilityActivated` payload
 * @param {object} gameState
 * @param {{ teleportTrailDuration?: number }} [services]
 *        client helpers an entry may need; passed in rather than imported so
 *        this module stays free of renderer and client dependencies
 */
export function notifyAbilityActivated(abilityId, player, data, gameState, services = {}) {
  registry.get(abilityId)?.onActivated?.(player, data, gameState, services);
}

/**
 * The mirror of `notifyAbilityActivated`, for deactivation and cancellation.
 * @param {string} abilityId
 * @param {object|null} player
 * @param {object} data
 * @param {object} gameState
 */
export function notifyAbilityDeactivated(abilityId, player, data, gameState, services = {}) {
  registry.get(abilityId)?.onDeactivated?.(player, data, gameState, services);
}

/**
 * A position jump must not be drawn as a movement trail streaking across the
 * map, so the abilities that move a player instantly break the trail.
 * @private
 */
function breakMovementTrail(data, gameState) {
  if (data.refreshed) return;
  gameState?.trailBreaks?.push(data.playerId);
}

/**
 * Set `<id>` / `<id>EndTime` on a remote player, which is how the renderer
 * knows an effect is running on someone else. The local player's own copy is
 * driven by the server's authoritative state, not by this.
 * @private
 */
function markRemoteEffect(abilityId, player, data, gameState) {
  if (!player || data.playerId === gameState?.localId) return;
  player.activeAbilityEffects = player.activeAbilityEffects || {};
  player.activeAbilityEffects[abilityId] = true;
  player.activeAbilityEffects[`${abilityId}EndTime`] = performance.now() + (data.duration || 0);
}

/** @private */
function clearEffect(abilityId, player) {
  if (!player?.activeAbilityEffects) return;
  delete player.activeAbilityEffects[abilityId];
  delete player.activeAbilityEffects[`${abilityId}EndTime`];
}

// ───────────────────────── built-in effects ─────────────────────────

// Invisibility: remote players fade out over effects.fadeMs, then vanish; the
// local player fades to 30%. The fade restarts each time the effect starts,
// including after an interact reveal, because onEnd clears the start time.
const INVISIBILITY_LOCAL_ALPHA = 0.3;
const INVISIBILITY_DEFAULT_FADE_MS = 400;

registerAbilityEffect('invisibility', {
  view(player, { now, group } = {}) {
    const target = player.isLocal ? INVISIBILITY_LOCAL_ALPHA : 0;
    const fadeMs = abilityRegistry.get('invisibility')?.effects?.fadeMs ?? INVISIBILITY_DEFAULT_FADE_MS;
    let progress = 1;
    if (group && typeof now === 'number' && fadeMs > 0) {
      group._invisibilityFadeStart ??= now;
      progress = Math.min(1, (now - group._invisibilityFadeStart) / fadeMs);
    }
    if (progress >= 1) return player.isLocal ? { alpha: target } : { visible: false };
    return { alpha: 1 + (target - 1) * progress };
  },
  onEnd(group) {
    delete group._invisibilityFadeStart;
  },
  onActivated: markRemoteEffect.bind(null, 'invisibility'),
  onDeactivated: (player) => clearEffect('invisibility', player)
});

// Teleport: no per-frame visual of its own — a trail break so the movement
// trail does not streak across the jump, plus the jump's own trail.
registerAbilityEffect('teleport', {
  onActivated(player, data, gameState, services = {}) {
    breakMovementTrail(data, gameState);
    if (data.refreshed || !data.teleportData || !gameState?.teleportTrails) return;

    gameState.teleportTrails.push({
      playerId: data.playerId,
      startX: data.teleportData.startX,
      startY: data.teleportData.startY,
      endX: data.teleportData.endX,
      endY: data.teleportData.endY,
      angle: data.teleportData.angle,
      startTime: performance.now(),
      duration: services.teleportTrailDuration ?? 400,
      skinId: player?.cosmetics?.equippedSkin || 'default'
    });
  }
});

// Rewind: only the activating player sees the snap-back marker (world overlay +
// HUD). Others still see trail breaks and the teleport trail on cancel.
registerAbilityEffect('rewind', {
  onActivated(player, data, gameState) {
    breakMovementTrail(data, gameState);
    if (!player || !data.effects || data.playerId !== gameState?.localId) return;

    // The marker position is what `onFrame` below draws, and what the HUD
    // reads out of abilityState.
    player.activeAbilityEffects = player.activeAbilityEffects || {};
    player.activeAbilityEffects.rewind = true;
    player.activeAbilityEffects.rewindStartX = data.effects.rewindStartX;
    player.activeAbilityEffects.rewindStartY = data.effects.rewindStartY;
    player.activeAbilityEffects.rewindStartAngle = data.effects.rewindStartAngle;

    const tracked = gameState?.abilityState?.activeEffects?.rewind;
    if (tracked) {
      tracked.rewindStartX = data.effects.rewindStartX;
      tracked.rewindStartY = data.effects.rewindStartY;
    }
  },

  onDeactivated(player, data = {}, gameState, services = {}) {
    if (player?.activeAbilityEffects) {
      delete player.activeAbilityEffects.rewind;
      delete player.activeAbilityEffects.rewindStartX;
      delete player.activeAbilityEffects.rewindStartY;
      delete player.activeAbilityEffects.rewindStartAngle;
    }

    // A cancelled rewind snaps the player back; everyone sees the jump,
    // wearing the player who snapped (skin, id, facing), not the viewer.
    if (!data.rewindData || !gameState?.teleportTrails) return;
    const { fromX, fromY, toX, toY, toAngle } = data.rewindData;
    gameState.teleportTrails.push({
      playerId: data.playerId,
      startX: fromX,
      startY: fromY,
      endX: toX,
      endY: toY,
      angle: typeof toAngle === 'number' ? toAngle : (player?.angle || 0),
      startTime: performance.now(),
      duration: services.teleportTrailDuration ?? 400,
      skinId: player?.cosmetics?.equippedSkin || 'default'
    });
  },

  onFrame(group, { now, player }) {
    if (!player?.isLocal) return;
    const overlay = group.worldOverlay;
    const fx = player.activeAbilityEffects || {};
    if (!overlay || typeof fx.rewindStartX !== 'number' || typeof fx.rewindStartY !== 'number') return;
    const pulse = 0.5 + Math.sin(now / 200) * 0.15;
    overlay.clear();
    overlay.circle(fx.rewindStartX, fx.rewindStartY, 36);
    overlay.stroke({ width: 3, color: 0x00d4ff, alpha: pulse });
    overlay.circle(fx.rewindStartX, fx.rewindStartY, 6);
    overlay.fill({ color: 0x00d4ff, alpha: pulse });
    overlay.visible = true;
  },
  onEnd(group) {
    if (group.worldOverlay) { group.worldOverlay.clear(); group.worldOverlay.visible = false; }
  }
});

// Invulnerability: pulsing gold ring on the rotation container's overlay.
registerAbilityEffect('invulnerability', {
  onActivated: markRemoteEffect.bind(null, 'invulnerability'),
  onDeactivated: (player) => clearEffect('invulnerability', player),

  onFrame(group, { now, alpha }) {
    const overlay = group.invulnOverlay;
    if (!overlay) return;
    const pulse = Math.sin(now / 150) * 0.15 + 0.35;
    overlay.clear();
    overlay.circle(0, 0, 38);
    overlay.fill({ color: 0xffd700, alpha: pulse * alpha });
    overlay.circle(0, 0, 42);
    overlay.stroke({ width: 3, color: 0xffd700, alpha: 0.8 * alpha });
    overlay.visible = true;
  },
  onEnd(group) {
    if (group.invulnOverlay) group.invulnOverlay.visible = false;
  }
});

registerAbilityEffect('nullify', {
  onActivated(_player, data, gameState) {
    if (!data.nullifyData) return;
    if (!gameState.nullifyRings) gameState.nullifyRings = [];
    gameState.nullifyRings.push({
      ...data.nullifyData,
      startTime: performance.now(),
      color: 0xd6c6ff
    });
  }
});
